import React, { useState } from 'react';
import { ArrowLeft, Plus } from 'lucide-react';

// Mock athletes
const mockAthletes = [
  { id: 1, name: 'Sam Wilson', club: 'Brisbane AC', bib: '230', sb: '12.20' },
  { id: 2, name: 'Lisa Park', club: 'Gold Coast AC', bib: '234', sb: '12.95' },
  { id: 3, name: 'Mike Jones', club: 'Springfield AC', bib: '189', sb: null },
  { id: 4, name: 'John Smith', club: 'Brisbane AC', bib: '245', sb: '12.45' },
];

const StartListEditor = () => {
  const [activeRound, setActiveRound] = useState('heats');
  const [selectedLanes, setSelectedLanes] = useState([1, 2, 3, 4, 5, 6, 7, 8]);
  const [minAthletes, setMinAthletes] = useState(2);
  const [maxAthletes, setMaxAthletes] = useState(8);
  const [heats, setHeats] = useState([
    {
      id: 1,
      lanes: {
        2: {
          id: 4,
          name: 'John Smith',
          club: 'Brisbane AC',
          bib: '245',
          sb: '12.45',
        },
        3: null,
        4: null,
        5: null,
        6: null,
        7: null,
        8: null,
      },
    },
    {
      id: 2,
      lanes: {
        1: null,
        2: null,
        3: null,
        4: null,
        5: null,
        6: null,
        7: null,
        8: null,
      },
    },
  ]);
  const [availableAthletes, setAvailableAthletes] = useState(
    mockAthletes.filter((a) => a.id !== 4)
  );
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [selectedLaneAthlete, setSelectedLaneAthlete] = useState(null);
  const [sortBy, setSortBy] = useState('sb');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAutoGenerate, setShowAutoGenerate] = useState(false);

  const toggleLane = (lane) => {
    setSelectedLanes((prev) =>
      prev.includes(lane)
        ? prev.filter((l) => l !== lane)
        : [...prev, lane].sort((a, b) => a - b)
    );
  };

  const handleLaneClick = (heatIndex, lane) => {
    const targetAthlete = heats[heatIndex].lanes[lane];

    // Case 1: Clicking on athlete in lane to select them
    if (targetAthlete && !selectedAthlete && !selectedLaneAthlete) {
      setSelectedLaneAthlete({ heatIndex, lane, athlete: targetAthlete });
      return;
    }

    // Case 2: Moving athlete from available to empty lane
    if (selectedAthlete && !targetAthlete) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[lane] = selectedAthlete;
      setHeats(newHeats);
      setAvailableAthletes((prev) =>
        prev.filter((a) => a.id !== selectedAthlete.id)
      );
      setSelectedAthlete(null);
      return;
    }

    // Case 3: Replacing athlete in occupied lane (from available)
    if (selectedAthlete && targetAthlete) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[lane] = selectedAthlete;
      setHeats(newHeats);
      setAvailableAthletes((prev) =>
        prev.filter((a) => a.id !== selectedAthlete.id).concat(targetAthlete)
      );
      setSelectedAthlete(null);
      return;
    }

    // Case 4: Moving athlete within same heat to empty lane
    if (
      selectedLaneAthlete &&
      !targetAthlete &&
      selectedLaneAthlete.heatIndex === heatIndex
    ) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[selectedLaneAthlete.lane] = null;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    // Case 5: Swapping two athletes within same heat
    if (
      selectedLaneAthlete &&
      targetAthlete &&
      selectedLaneAthlete.heatIndex === heatIndex
    ) {
      const newHeats = [...heats];
      newHeats[heatIndex].lanes[selectedLaneAthlete.lane] = targetAthlete;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    // Case 6: Moving athlete to empty lane in different heat
    if (
      selectedLaneAthlete &&
      !targetAthlete &&
      selectedLaneAthlete.heatIndex !== heatIndex
    ) {
      const newHeats = [...heats];
      newHeats[selectedLaneAthlete.heatIndex].lanes[selectedLaneAthlete.lane] =
        null;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    // Case 7: Swapping athletes between different heats
    if (
      selectedLaneAthlete &&
      targetAthlete &&
      selectedLaneAthlete.heatIndex !== heatIndex
    ) {
      const newHeats = [...heats];
      newHeats[selectedLaneAthlete.heatIndex].lanes[selectedLaneAthlete.lane] =
        targetAthlete;
      newHeats[heatIndex].lanes[lane] = selectedLaneAthlete.athlete;
      setHeats(newHeats);
      setSelectedLaneAthlete(null);
      return;
    }

    // Case 8: Deselect if clicking same lane again
    if (
      selectedLaneAthlete &&
      selectedLaneAthlete.heatIndex === heatIndex &&
      selectedLaneAthlete.lane === lane
    ) {
      setSelectedLaneAthlete(null);
      return;
    }
  };

  const removeAthlete = (heatIndex, lane) => {
    const athlete = heats[heatIndex].lanes[lane];
    if (!athlete) return;

    const newHeats = [...heats];
    newHeats[heatIndex].lanes[lane] = null;
    setHeats(newHeats);
    setAvailableAthletes((prev) => [...prev, athlete]);
    setSelectedLaneAthlete(null);
  };

  const addHeat = () => {
    const newHeat = {
      id: heats.length + 1,
      lanes: Object.fromEntries(selectedLanes.map((l) => [l, null])),
    };
    setHeats([...heats, newHeat]);
  };

  const removeHeat = (heatIndex) => {
    if (heatIndex === 0) return;

    const heat = heats[heatIndex];
    const athletesToReturn = Object.values(heat.lanes).filter(
      (a) => a !== null
    );

    setHeats((prev) => prev.filter((_, i) => i !== heatIndex));
    setAvailableAthletes((prev) => [...prev, ...athletesToReturn]);
    setSelectedLaneAthlete(null);
  };

  const clearHeat = (heatIndex) => {
    const heat = heats[heatIndex];
    const athletesToReturn = Object.values(heat.lanes).filter(
      (a) => a !== null
    );

    const newHeats = [...heats];
    newHeats[heatIndex].lanes = Object.fromEntries(
      selectedLanes.map((l) => [l, null])
    );
    setHeats(newHeats);
    setAvailableAthletes((prev) => [...prev, ...athletesToReturn]);
  };

  const assignedCount = heats.reduce((total, heat) => {
    return total + Object.values(heat.lanes).filter((a) => a !== null).length;
  }, 0);

  const filteredAvailable = availableAthletes.filter(
    (a) =>
      a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.club.toLowerCase().includes(searchQuery.toLowerCase()) ||
      a.bib.includes(searchQuery)
  );

  return (
    <div
      style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '24px',
          borderBottom: '2px solid #e5e7eb',
        }}
      >
        <button
          style={{
            background: 'transparent',
            border: 'none',
            color: '#3b82f6',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginBottom: '12px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={16} />
          Back to Event List
        </button>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <div>
            <h1
              style={{ fontSize: '24px', fontWeight: 600, margin: '0 0 4px 0' }}
            >
              U12 Male - 100m Sprint
            </h1>
            <p style={{ color: '#6b7280', margin: 0 }}>
              28 registered athletes
            </p>
          </div>
        </div>
      </div>

      {/* Round Tabs */}
      <div
        style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '8px',
        }}
      >
        {['heats', 'semifinals', 'finals'].map((round) => (
          <button
            key={round}
            onClick={() => setActiveRound(round)}
            style={{
              padding: '8px 20px',
              background: activeRound === round ? '#3b82f6' : 'white',
              color: activeRound === round ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              textTransform: 'capitalize',
            }}
          >
            {round}
          </button>
        ))}
      </div>

      {/* Assignment Settings */}
      <div
        style={{
          padding: '20px 24px',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: 600,
            color: '#374151',
            marginBottom: '12px',
          }}
        >
          Assignment Settings
        </div>

        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          {/* Available Lanes */}
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '12px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '8px',
              }}
            >
              Available Lanes for Heats
            </label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((lane) => (
                <button
                  key={lane}
                  onClick={() => toggleLane(lane)}
                  style={{
                    width: '36px',
                    height: '36px',
                    background: selectedLanes.includes(lane)
                      ? '#3b82f6'
                      : 'white',
                    color: selectedLanes.includes(lane) ? 'white' : '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  {lane}
                </button>
              ))}
            </div>
          </div>

          {/* Athletes per heat */}
          <div style={{ display: 'flex', gap: '16px' }}>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                Min Athletes
              </label>
              <select
                value={minAthletes}
                onChange={(e) => setMinAthletes(Number(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '80px',
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8]
                  .filter((n) => n <= maxAthletes)
                  .map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '8px',
                }}
              >
                Max Athletes
              </label>
              <select
                value={maxAthletes}
                onChange={(e) => setMaxAthletes(Number(e.target.value))}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '14px',
                  width: '80px',
                }}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8]
                  .filter((n) => n >= minAthletes && n <= selectedLanes.length)
                  .map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div
        style={{
          padding: '16px 24px',
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={addHeat}
            style={{
              padding: '8px 16px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Plus size={16} />
            Add Heat
          </button>
          <button
            onClick={() => setShowAutoGenerate(true)}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Auto Generate
          </button>
          <button
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#dc2626',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Main Editor */}
      <div
        style={{
          padding: '24px',
          display: 'flex',
          gap: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Fixed Column - Available Athletes */}
        <div
          style={{
            position: 'sticky',
            left: 0,
            width: '280px',
            flexShrink: 0,
            background: 'white',
            borderRight: '2px solid #e5e7eb',
            paddingRight: '12px',
          }}
        >
          <div
            style={{
              padding: '12px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '6px',
              color: 'white',
              fontWeight: 600,
              fontSize: '14px',
              marginBottom: '12px',
            }}
          >
            Available Athletes ({availableAthletes.length})
          </div>

          {/* Search & Sort */}
          <div style={{ marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
                marginBottom: '8px',
              }}
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                width: '100%',
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                fontSize: '13px',
              }}
            >
              <option value="sb">Sort: SB ▼</option>
              <option value="name">Sort: Name ▼</option>
              <option value="age">Sort: Age ▼</option>
            </select>
          </div>

          {/* Athletes List */}
          <div style={{ height: '500px', overflowY: 'auto' }}>
            {filteredAvailable.map((athlete) => (
              <div
                key={athlete.id}
                onClick={() => {
                  setSelectedAthlete(athlete);
                  setSelectedLaneAthlete(null);
                }}
                style={{
                  padding: '10px',
                  marginBottom: '6px',
                  background:
                    selectedAthlete?.id === athlete.id ? '#dbeafe' : '#f9fafb',
                  border: `2px solid ${selectedAthlete?.id === athlete.id ? '#3b82f6' : '#e5e7eb'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '2px' }}>
                  {athlete.name}
                </div>
                <div style={{ color: '#6b7280', fontSize: '12px' }}>
                  SB: {athlete.sb || 'N/A'} • Club: {athlete.club}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Heats Section */}
        <div style={{ flex: 1, overflowX: 'auto' }}>
          <div
            style={{ display: 'flex', gap: '12px', minWidth: 'fit-content' }}
          >
            {heats.map((heat, heatIndex) => (
              <div
                key={heat.id}
                style={{
                  width: '200px',
                  flexShrink: 0,
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                {/* Heat Header */}
                <div
                  style={{
                    padding: '12px',
                    background:
                      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '14px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>
                    Heat {heat.id} (
                    {Object.values(heat.lanes).filter((a) => a).length}/
                    {selectedLanes.length})
                  </span>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => clearHeat(heatIndex)}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: 'none',
                        color: 'white',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        padding: '2px 6px',
                        fontSize: '11px',
                      }}
                    >
                      Clear
                    </button>
                    {heatIndex > 0 && (
                      <button
                        onClick={() => removeHeat(heatIndex)}
                        style={{
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          padding: '2px 6px',
                          fontSize: '11px',
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Lanes */}
                <div style={{ padding: '12px', background: '#f9fafb' }}>
                  {selectedLanes.map((lane) => {
                    const isSelected =
                      selectedLaneAthlete?.heatIndex === heatIndex &&
                      selectedLaneAthlete?.lane === lane;
                    return (
                      <div
                        key={lane}
                        onClick={() => handleLaneClick(heatIndex, lane)}
                        style={{
                          padding: '10px',
                          marginBottom: '6px',
                          background: isSelected
                            ? '#fef3c7'
                            : heat.lanes[lane]
                              ? '#eff6ff'
                              : '#fff',
                          border: `2px ${heat.lanes[lane] || isSelected ? 'solid' : 'dashed'} ${isSelected ? '#f59e0b' : heat.lanes[lane] ? '#3b82f6' : '#d1d5db'}`,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          minHeight: '50px',
                          position: 'relative',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            color: '#6b7280',
                            marginBottom: '4px',
                          }}
                        >
                          Lane {lane}
                        </div>
                        {heat.lanes[lane] ? (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: '13px' }}>
                              {heat.lanes[lane].name}
                            </div>
                            <div style={{ color: '#6b7280', fontSize: '11px' }}>
                              {heat.lanes[lane].sb || 'No SB'} • #
                              {heat.lanes[lane].bib}
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeAthlete(heatIndex, lane);
                              }}
                              style={{
                                position: 'absolute',
                                top: '4px',
                                right: '4px',
                                background: '#fee2e2',
                                color: '#dc2626',
                                border: 'none',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                padding: '2px 6px',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>
                            Empty
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div
        style={{
          padding: '16px 24px',
          background: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          Total: {mockAthletes.length} athletes | Assigned: {assignedCount} |
          Unassigned: {availableAthletes.length}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={{
              padding: '8px 20px',
              background: 'white',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Discard Changes
          </button>
          <button
            style={{
              padding: '8px 20px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Save All Changes
          </button>
        </div>
      </div>

      {/* Auto Generate Modal */}
      {showAutoGenerate && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              padding: '24px',
              width: '400px',
              maxWidth: '90%',
            }}
          >
            <h3
              style={{
                margin: '0 0 16px 0',
                fontSize: '18px',
                fontWeight: 600,
              }}
            >
              Generate Heats
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <div
                style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginBottom: '12px',
                }}
              >
                28 athletes to distribute
              </div>

              <label
                style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 500,
                  marginBottom: '8px',
                }}
              >
                Number of heats: [4 ▼]
              </label>

              <div style={{ marginTop: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                  }}
                >
                  Seeding Method:
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '6px',
                  }}
                >
                  <input type="radio" name="seeding" defaultChecked />
                  <span style={{ fontSize: '14px' }}>Fastest heat last</span>
                </label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <input type="radio" name="seeding" />
                  <span style={{ fontSize: '14px' }}>Random</span>
                </label>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '8px',
                  }}
                >
                  Lane Assignment:
                </label>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '6px',
                  }}
                >
                  <input type="radio" name="lanes" defaultChecked />
                  <span style={{ fontSize: '14px' }}>
                    Fastest in center lanes
                  </span>
                </label>
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <input type="radio" name="lanes" />
                  <span style={{ fontSize: '14px' }}>Random</span>
                </label>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                marginTop: '24px',
              }}
            >
              <button
                onClick={() => setShowAutoGenerate(false)}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => setShowAutoGenerate(false)}
                style={{
                  padding: '8px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StartListEditor;
